import React, { useEffect, useState } from "react";
import LazyRabbitSign from "./RabbitSign";
import axios from "axios";

export default function DocumentESignatureScreen({
  onPrevious,
  onNext,
  workflowData,
  insuredId,
  loading,
}: {
  onPrevious?: () => void;
  onNext?: (arg: { status: string }) => void;
  workflowData: any;
  insuredId: string;
  loading?: boolean;
}) {
  // Track which button is loading: 'waiting' | 'continue' | null
  const [buttonLoading, setButtonLoading] = useState<null | "waiting" | "continue" | "complete">(null);
  const [showEmailDialog, setShowEmailDialog] = useState(false);
  const [inputEmail, setInputEmail] = useState("");
  const [pendingDatabaseId, setPendingDatabaseId] = useState<string | null>(null);
  useEffect(() => {
    const layoutContent = document.querySelector(".layout-content");
    if (layoutContent) {
      layoutContent.scrollTo({ top: 0, behavior: "auto" });
    }
    window.scrollTo({ top: 0, behavior: "auto" });
  }, []);

  const dummyDocuments = workflowData.signDocumentFileIds || [];
  const [viewDocUrl, setViewDocUrl] = useState<string | null>(null);

  // Button handlers: just send status to parent
  const handleWaitingForESignature = () => {
    setButtonLoading("waiting");
    if (onNext) {
      Promise.resolve(onNext({ status: "Waiting for E-Signature" })).finally(() => setButtonLoading(null));
    } else {
      setButtonLoading(null);
    }
  };
  const handleContinue = () => {
    setButtonLoading("continue");
    if (onNext) {
      Promise.resolve(onNext({ status: "In Progress" })).finally(() => setButtonLoading(null));
    } else {
      setButtonLoading(null);
    }
  };
  const handleMarkAsComplete = () => {
    setButtonLoading("complete");
    if (onNext) {
      Promise.resolve(onNext({ status: "Completed" })).finally(() => setButtonLoading(null));
    } else {
      setButtonLoading(null);
    }
  };
  const onPreviewDocumentClicked = (databaseId: string) => {
    const email = workflowData?.step1?.email;
    if (email && email.trim() !== "") {
      let config = {
        method: "get",
        maxBodyLength: Infinity,
        url: `${process.env.REACT_APP_Nowcerts_Base_Url}/api/IGToken/GetToken`,
        headers: {
          Authorization: "Bearer " + localStorage.getItem("NCToken"),
        },
      };
      axios
        .request(config)
        .then((response) => {
          const agentEmail = localStorage.getItem("agentEmail") ?? "";
          const agentName = localStorage.getItem("name") ?? "";
          const fromEmail = localStorage.getItem("userName") ?? "";
          const signers = `${email},${workflowData.step1.firstName} ${workflowData.step1.lastName},1|${agentEmail},${agentName},4`;
          const url =
            `${process.env.REACT_APP_RabbitSignExternal_Base_Url}` +
            `?igtoken=${encodeURIComponent(response.data)}` +
            "&theme=light" +
            `&fileId=${encodeURIComponent(databaseId)}` +
            `&insuredId=${encodeURIComponent(insuredId)}` +
            `&signers=${encodeURIComponent(signers)}` +
            `&fromEmail=${encodeURIComponent(fromEmail)}` +
            "&emailSubject=Please%20sign%20this%20document" +
            "&emailBody=Please%20sign%20this%20document";
          window.open(url, "_blank");
        })
        .catch((error) => {
          console.log(error);
        });
    } else {
      setPendingDatabaseId(databaseId);
      setShowEmailDialog(true);
    }
  };

  const handleEmailDialogSubmit = () => {
    if (!inputEmail || inputEmail.trim() === "") return;
    setShowEmailDialog(false);
    if (!pendingDatabaseId) return;
    let config = {
      method: "get",
      maxBodyLength: Infinity,
      url: `${process.env.REACT_APP_Nowcerts_Base_Url}/api/IGToken/GetToken`,
      headers: {
        Authorization: "Bearer " + localStorage.getItem("NCToken"),
      },
    };
    axios
      .request(config)
      .then((response) => {
        const agentEmail = localStorage.getItem("agentEmail") ?? "";
        const agentName = localStorage.getItem("name") ?? "";
        const signers = `${inputEmail},${workflowData.step1.firstName} ${workflowData.step1.lastName},1|${agentEmail},${agentName},4`;
        const url =
          `${process.env.REACT_APP_RabbitSignExternal_Base_Url}` +
          `?igtoken=${encodeURIComponent(response.data)}` +
          "&theme=light" +
          `&fileId=${encodeURIComponent(pendingDatabaseId)}` +
          `&insuredId=${encodeURIComponent(insuredId)}` +
          `&signers=${encodeURIComponent(signers)}` +
          `&fromEmail=${encodeURIComponent(agentEmail)}` +
          "&emailSubject=Please%20sign%20this%20document" +
          "&emailBody=Please%20sign%20this%20document";
        window.open(url, "_blank");
      })
      .catch((error) => {
        console.log(error);
      });
    setInputEmail("");
    setPendingDatabaseId(null);
  };

  return (
    <div className="page-container">
      <div className="page-header mb-4">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold">Document E-Signature</h1>
            <p className="text-sm text-gray-500">
              Review all ancillary policy forms and agency fee disclosures. Select documents to send for e-signature using RabitSign.
            </p>
          </div>
        </div>
      </div>
      <div className="form-grid">
        <form className="esignature-form">
          <div className="form-section-header form-group full-width">
            <h3 className="text-lg font-semibold mb-2">Documents for E-Signature</h3>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {dummyDocuments.filter((doc: any) => doc.status === "1").length === 0 ? (
              <div className="text-gray-500">No documents available.</div>
            ) : (
              dummyDocuments.filter((doc: any) => doc.status === "1").map((doc: any, index: any) => (
                <div key={doc.databaseId} className="bg-white rounded-lg shadow p-4 flex flex-col gap-2 border border-gray-200">
                  <div className="flex items-center justify-between">
                    <span className="font-medium flex items-center gap-2">
                      <span className="text-base">{index + 1} - Sign Document</span>
                      <span className="text-xs text-gray-400">PDF</span>
                    </span>
                    <button type="button" onClick={() => onPreviewDocumentClicked(doc.databaseId)} style={{ color: "#6352ff", cursor: "pointer" }}>
                      Preview & Send
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>

          <div className="form-actions mt-8" style={{ display: "flex", alignItems: "center" }}>
            <button type="button" className="btn btn-secondary" onClick={onPrevious}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" stroke="currentColor" />
              </svg>
              <span>Previous</span>
            </button>

            <div style={{ display: "flex", gap: "12px", marginLeft: "auto" }}>
              {/* <button type="button" className="btn btn-primary" onClick={handleWaitingForESignature} disabled={buttonLoading !== null}>
                {buttonLoading === "waiting" ? (
                  "Saving..."
                ) : (
                  <>
                    Waiting for E-Signature
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3.5" d="M9 5l7 7-7 7" stroke="currentColor" />
                    </svg>
                  </>
                )}
              </button>

              <button type="button" className="btn btn-primary" onClick={handleContinue} disabled={buttonLoading !== null}>
                {buttonLoading === "continue" ? (
                  "Saving..."
                ) : (
                  <>
                    Continue
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3.5" d="M9 5l7 7-7 7" stroke="currentColor" />
                    </svg>
                  </>
                )}
              </button> */}

              <button type="button" className="btn btn-primary" onClick={handleMarkAsComplete} disabled={buttonLoading !== null}>
                {buttonLoading === "complete" ? (
                  "Saving..."
                ) : (
                  <>
                    Continue
                   <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3.5" d="M9 5l7 7-7 7" stroke="currentColor" />
                    </svg>
                  </>
                )}
              </button>
            </div>
          </div>
        </form>
        {viewDocUrl && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg shadow-lg p-6 w-full max-w-2xl relative">
              <h3 className="text-lg font-semibold mb-4">Document Preview</h3>
              <iframe src={viewDocUrl} title="Document Preview" width="100%" height="500px" className="rounded border" />
              <button className="btn btn btn-secondary mt-4 absolute top-4 right-4" onClick={() => setViewDocUrl(null)}>
                &times;
              </button>
            </div>
          </div>
        )}
        {showEmailDialog && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg shadow-lg p-6 w-full max-w-sm relative">
              <h3 className="text-lg font-semibold mb-4">Enter Email for E-Signature</h3>
              <input
                type="email"
                className="form-input compact-input w-full mb-4"
                placeholder="Enter email address"
                value={inputEmail}
                onChange={(e) => setInputEmail(e.target.value)}
                autoFocus
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    handleEmailDialogSubmit();
                  }
                }}
              />
              <div className="flex justify-end gap-2">
                <button
                  className="btn btn-secondary"
                  onClick={() => {
                    setShowEmailDialog(false);
                    setInputEmail("");
                    setPendingDatabaseId(null);
                  }}
                >
                  Cancel
                </button>
                <button className="btn btn-primary" onClick={handleEmailDialogSubmit}>
                  Continue
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
